// Aliased to flarumExtend to prevent naming collision with the Flarum 2.x frontend
// extender array exported as `extend` below, as this repo consolidates both in forum.ts.
import { extend as flarumExtend } from 'flarum/common/extend';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import UserControls from 'flarum/forum/utils/UserControls';
import LinkButton from 'flarum/common/components/LinkButton';
import Button from 'flarum/common/components/Button';
import SupportCategory from './common/models/SupportCategory';
import SupportTicket from './common/models/SupportTicket';
import SupportReply from './common/models/SupportReply';
import SupportEvent from './common/models/SupportEvent';
import SupportSavedReply from './common/models/SupportSavedReply';
import { NewSupportReplyNotification, NewSupportTicketNotification, TicketAssignedNotification, AwaitingReminderNotification, TicketStatusChangedNotification, installSupportNotificationGrouping } from './forum/components/notifications';
import Extend from 'flarum/common/extenders';
import { tr } from './forum/utils/translate';
import { installRealtime } from './forum/utils/live';
import { basePath, BASE_PATH, showNavInSidebar, showNavInAccountMenu, readForumAttribute, showError } from './forum/utils/helpers';
import { ticketTypes, TicketTypeRegistry } from './common/ticketTypes';
import { SUPPORT_TICKETS_RESOURCE, StatusGambit, AssignedGambit, MineGambit, PriorityGambit, CategoryGambit, TicketTypeGambit, AuthorGambit } from './forum/query/ticketGambits';
import addSupportSearch from './forum/addSupportSearch';
export { ticketTypes, TicketTypeRegistry };
export const extend = [new Extend.Search().gambit(SUPPORT_TICKETS_RESOURCE, StatusGambit).gambit(SUPPORT_TICKETS_RESOURCE, AssignedGambit).gambit(SUPPORT_TICKETS_RESOURCE, MineGambit).gambit(SUPPORT_TICKETS_RESOURCE, PriorityGambit).gambit(SUPPORT_TICKETS_RESOURCE, CategoryGambit).gambit(SUPPORT_TICKETS_RESOURCE, TicketTypeGambit).gambit(SUPPORT_TICKETS_RESOURCE, AuthorGambit)];
app.initializers.add('linkrobins-support', () => {
  app.support = {
    ticketTypes
  };
  addSupportSearch();

  // Register the store models so app.store.find()/createRecord() return typed,
  // cached, relationship-aware records for our resources.
  app.store.models['linkrobins-support-categories'] = SupportCategory;
  app.store.models['linkrobins-support-tickets'] = SupportTicket;
  app.store.models['linkrobins-support-replies'] = SupportReply;
  app.store.models['linkrobins-support-events'] = SupportEvent;
  app.store.models['linkrobins-support-saved-replies'] = SupportSavedReply;

  // The desk's pages load on demand, as their own chunks: a visitor reading a
  // discussion never opens them, and parsing them on every page costs phones
  // time. extend.php publishes the chunks with ->jsDirectory(); without it
  // they 404 and every support route falls back to the home page.
  app.routes['linkrobins-support.index'] = {
    path: BASE_PATH,
    component: () => import(/* webpackChunkName: 'forum/components/SupportIndexPage', webpackMode: 'lazy-once' */ './forum/components/SupportIndexPage')
  };
  app.routes['linkrobins-support.search'] = {
    path: BASE_PATH + '/search',
    component: () => import(/* webpackChunkName: 'forum/components/SupportSearchPage', webpackMode: 'lazy-once' */ './forum/components/SupportSearchPage')
  };
  app.routes['linkrobins-support.compose'] = {
    path: BASE_PATH + '/new',
    component: () => import(/* webpackChunkName: 'forum/components/SupportComposePage', webpackMode: 'lazy-once' */ './forum/components/SupportComposePage')
  };
  app.routes['linkrobins-support.filtered'] = {
    path: BASE_PATH + '/status/:status',
    component: () => import(/* webpackChunkName: 'forum/components/SupportIndexPage', webpackMode: 'lazy-once' */ './forum/components/SupportIndexPage')
  };
  app.routes['linkrobins-support.show'] = {
    path: BASE_PATH + '/:id',
    component: () => import(/* webpackChunkName: 'forum/components/SupportShowPage', webpackMode: 'lazy-once' */ './forum/components/SupportShowPage')
  };
  if (app.notificationComponents) {
    app.notificationComponents['linkrobinsSupportNewReply'] = NewSupportReplyNotification;
    app.notificationComponents['linkrobinsSupportNewTicket'] = NewSupportTicketNotification;
    app.notificationComponents['linkrobinsSupportTicketStatusChanged'] = TicketStatusChangedNotification;
    app.notificationComponents['linkrobinsSupportTicketAssigned'] = TicketAssignedNotification;
    app.notificationComponents['linkrobinsSupportAwaitingReminder'] = AwaitingReminderNotification;
  }

  // Group support notifications under a translatable "Support" heading in the
  // notifications dropdown, instead of the generic forum-title group.
  installSupportNotificationGrouping();

  // Live replies and ticket updates, when flarum/realtime is enabled.
  installRealtime();

  // NotificationGrid lives in a lazily-loaded chunk, so it isn't in the
  // registry at init time -- a direct import resolves to undefined. Use the
  // string-path form of extend(), which defers resolution until the module
  // actually loads.
  flarumExtend('flarum/forum/components/NotificationGrid', 'notificationTypes', items => {
    items.add('linkrobinsSupportNewReply', {
      name: 'linkrobinsSupportNewReply',
      icon: 'fas fa-life-ring',
      label: tr('settings.notify_new_reply_label', 'Someone replies to your support ticket')
    });
    items.add('linkrobinsSupportNewTicket', {
      name: 'linkrobinsSupportNewTicket',
      icon: 'fas fa-ticket-alt',
      label: tr('settings.notify_new_ticket_label', 'A new support ticket is opened')
    });
    items.add('linkrobinsSupportTicketStatusChanged', {
      name: 'linkrobinsSupportTicketStatusChanged',
      icon: 'fas fa-exchange-alt',
      label: tr('settings.notify_status_changed_label', "A support ticket's status changes")
    });
    items.add('linkrobinsSupportTicketAssigned', {
      name: 'linkrobinsSupportTicketAssigned',
      icon: 'fas fa-user-check',
      label: tr('settings.notify_assigned_label', 'A support ticket is assigned to you')
    });
    items.add('linkrobinsSupportAwaitingReminder', {
      name: 'linkrobinsSupportAwaitingReminder',
      icon: 'fas fa-hourglass-half',
      label: tr('settings.notify_awaiting_reminder_label', 'Support has been waiting a few days for my reply')
    });
  });

  // Appeal-ban toggle in the user's moderation-controls dropdown (the same menu
  // as Suspend). Shown only to users with the manage_appeal_bans permission.
  // UserControls is a plain object (not a component class), so we extend the
  // object itself -- extending `.prototype` would silently no-op.
  flarumExtend(UserControls, 'moderationControls', (items, user) => {
    if (!readForumAttribute('canManageSupportAppealBans')) return;
    if (!user || typeof user.attribute !== 'function') return;
    const banned = !!user.attribute('supportAppealBanned');
    items.add('linkrobinsSupportAppealBan', m(Button, {
      icon: banned ? 'fas fa-unlock' : 'fas fa-ban',
      onclick: () => {
        user.save({
          supportAppealBanned: !banned
        }).then(() => {
          m.redraw();
        }).catch(() => {
          showError(tr('user_controls.toggle_failed', 'Could not update the appeal-ban status.'));
        });
      }
    }, banned ? tr('user_controls.allow_appeals', 'Allow support appeals') : tr('user_controls.disallow_appeals', 'Disallow support appeals')));
  });
  const supportLink = () => m(LinkButton, {
    href: basePath() + BASE_PATH,
    icon: 'fas fa-life-ring'
  }, tr('nav', 'Support'));

  // Support link in the forum's sidebar nav. Priority -11 slots it directly
  // below flarum/tags' "Tags" link (-10) and above its separator (-12) and tag
  // list (-14), so it doesn't sit oddly between "All Discussions" and the tags
  // block. Without the tags extension it simply lands under the remaining nav
  // links.
  flarumExtend(IndexSidebar.prototype, 'navItems', items => {
    if (!app.session || !app.session.user || !showNavInSidebar()) return;
    items.add('linkrobins-support', supportLink(), -11);
  });

  // And in the account menu, under Settings (50) and above Administration (0),
  // with the other entries that are about you rather than about the forum.
  // String-path extend for the same reason as NotificationGrid above: it
  // defers resolution until the module is actually loaded.
  flarumExtend('flarum/forum/components/SessionDropdown', 'items', items => {
    if (!app.session || !app.session.user || !showNavInAccountMenu()) return;
    items.add('linkrobins-support', supportLink(), 40);
  });

  // Support stats for staff, in the account menu beside the Support link,
  // opening a modal the way LR Birdseye's Analytics item does.
  flarumExtend('flarum/forum/components/SessionDropdown', 'items', items => {
    if (!app.session || !app.session.user || !readForumAttribute('canHandleSupportTickets')) return;
    items.add('linkrobins-support-stats', m(Button, {
      icon: 'fas fa-chart-bar',
      onclick: () => app.modal.show(() => import(/* webpackChunkName: 'forum/components/SupportStatsModal', webpackMode: 'lazy-once' */ './forum/components/SupportStatsModal'))
    }, tr('stats.title', 'Support stats')), 39);
  });
});
flarum.reg.add('linkrobins-support', 'forum', { ticketTypes: ticketTypes,TicketTypeRegistry: TicketTypeRegistry,extend: extend, });flarum.reg.addChunkModule('458', '236', 'linkrobins-support', 'forum/components/SupportIndexPage');
flarum.reg.addChunkModule('780', '688', 'linkrobins-support', 'forum/components/SupportSearchPage');
flarum.reg.addChunkModule('190', '208', 'linkrobins-support', 'forum/components/SupportComposePage');
flarum.reg.addChunkModule('907', '426', 'linkrobins-support', 'forum/components/SupportShowPage');
flarum.reg.addChunkModule('993', '707', 'linkrobins-support', 'forum/components/SupportStatsModal');
flarum.reg.addChunkModule('458', '236', 'linkrobins-support', 'forum/components/SupportIndexSidebar');
flarum.reg.addChunkModule('458', '236', 'linkrobins-support', 'forum/components/SupportTicketRow');
flarum.reg.addChunkModule('190', '208', 'linkrobins-support', 'forum/utils/composer');
flarum.reg.addChunkModule('907', '426', 'linkrobins-support', 'forum/components/TicketHeader');
flarum.reg.addChunkModule('907', '426', 'linkrobins-support', 'forum/components/ReplyItem');
flarum.reg.addChunkModule('907', '426', 'linkrobins-support', 'forum/components/TicketEventItem');
flarum.reg.addChunkModule('907', '426', 'linkrobins-support', 'forum/components/SavedRepliesMenu');
flarum.reg.addChunkModule('907', '426', 'linkrobins-support', 'forum/components/ReplyComposer');
flarum.reg.addChunkModule('907', '426', 'linkrobins-support', 'forum/components/AssignTicketModal');